import { bound } from "@frui.ts/helpers";
import { PropertyName } from "@frui.ts/helpers/dist";
import { BusyWatcher, watchBusy } from "@frui.ts/screens";
import { validate } from "@frui.ts/validation";
import * as Sentry from "@sentry/react";
import cloneDeep from "lodash/cloneDeep";
import { action, computed, observable, toJS } from "mobx";
import type { EntityNames } from "../config";
import type { UpdatedEvent } from "../services/eventBus";
import type EventBus from "../services/eventBus";
import type NotificationService from "../services/notificationService";
import { NotificationType } from "../services/notificationService";
import type SecurityService from "../services/securityService";

export default abstract class EditableRowViewModel<TEntity> {
  busyWatcher = new BusyWatcher();
  @observable item: TEntity;
  protected originalItem: TEntity;
  @observable protected _editedProperty?: PropertyName<TEntity>;
  protected abstract readonly modelName: EntityNames;
  protected abstract notificationService: NotificationService;
  protected abstract eventBus: EventBus;
  protected abstract securityService: SecurityService;
  protected abstract updateData(): Promise<void>;
  abstract translate(code: string, size?: number): string;

  @computed
  get isEditable() {
    return this.securityService.allowToEntity(this.modelName, "write");
  }

  set editedProperty(property: PropertyName<TEntity> | undefined) {
    this._editedProperty = property;
  }

  @computed
  get editedProperty() {
    return this._editedProperty;
  }

  constructor(item: TEntity) {
    this.originalItem = item;
    this.setItem(item);
  }

  protected setItem(item: TEntity) {
    this.item = cloneDeep(toJS(item));
  }

  @bound
  @watchBusy
  async save() {
    if (validate(this.item) && this.isEditable) {
      try {
        await this.updateData();
        this.onUpdated();
      } catch (e) {
        Sentry.captureException(e);
      }
    }
  }

  protected onUpdated() {
    this.notificationService.add({ title: this.translate("updated"), type: NotificationType.SUCCESS });
    this.eventBus.publish<UpdatedEvent<TEntity>>({ type: `${this.modelName}.updated`, payload: { [this.modelName]: this.item } });
    this.onClose();
  }

  @action.bound
  onClose() {
    this.editedProperty = undefined;
  }
}
